/**
 * MRR de la serie con el movimiento del mes debajo: barras de nuevo y expansión
 * hacia arriba, churn hacia abajo, y la línea del MRR encima.
 *
 * Recharts y no un SVG a mano porque este gráfico tiene ejes; el sparkline de las
 * tarjetas sigue siendo el SVG propio del design system. El ancho se mide con
 * `useAncho` en vez de usar `ResponsiveContainer`: ver el comentario de ese hook.
 *
 * Dos decisiones:
 *  - **Un mes sin MRR cargado no se dibuja en cero.** `connectNulls` queda en
 *    false: un mes vacío corta la línea, porque un cero dibujado se lee como un
 *    mes en el que la empresa no facturó.
 *  - El gráfico es un `role="img"` con resumen en texto y su contenido queda
 *    `aria-hidden`: los números exactos están en la tabla mensual, que es la
 *    versión accesible y auditable de esta misma serie.
 */
import {
  formatMetricValue,
  formatPeriodo,
  formatPeriodoCorto,
  type RevenueMonth,
} from '@ops/contracts'
import { useRef } from 'react'
import { Bar, CartesianGrid, ComposedChart, Line, XAxis, YAxis } from 'recharts'

import { CHART } from '../../ui'
import { useAncho } from './useAncho'

export interface TendenciaMrrProps {
  meses: readonly RevenueMonth[]
}

interface Punto {
  periodo: string
  mrr: number | null
  nuevo: number | null
  expansion: number | null
  /** Negativo a propósito: el churn se dibuja hacia abajo del eje. */
  churn: number | null
}

const ALTO = 196

export function TendenciaMrr({ meses }: TendenciaMrrProps) {
  const contenedor = useRef<HTMLDivElement>(null)
  const ancho = useAncho(contenedor)
  const datos: Punto[] = meses.map((mes) => ({
    periodo: mes.periodo,
    mrr: mes.mrr,
    nuevo: mes.nuevoMrr,
    expansion: mes.expansionMrr,
    churn: mes.churnedMrr === null ? null : -mes.churnedMrr,
  }))

  const conDato = meses.filter((mes) => mes.mrr !== null)
  const primero = conDato[0]
  const ultimo = conDato.at(-1)

  const resumen =
    primero === undefined || ultimo === undefined
      ? 'Serie de MRR sin ningún mes cargado.'
      : `MRR de ${formatPeriodo(primero.periodo)} a ${formatPeriodo(ultimo.periodo)}: de ${formatMetricValue(primero.mrr, 'usd')} a ${formatMetricValue(ultimo.mrr, 'usd')}. Los valores exactos de cada mes están en la tabla de la serie mensual.`

  return (
    <figure className="m-0 flex min-w-0 flex-col gap-1.5">
      <figcaption className="rotulo">
        MRR y movimiento del mes · {meses.length} meses
      </figcaption>
      <div
        ref={contenedor}
        role="img"
        aria-label={resumen}
        className="w-full"
        style={{ height: ALTO }}
      >
        <div aria-hidden="true">
          <ComposedChart
            width={ancho}
            height={ALTO}
            data={datos}
            margin={{ top: 4, right: 4, bottom: 0, left: 4 }}
          >
            <CartesianGrid stroke={CHART.grid} vertical={false} />
            <XAxis
              dataKey="periodo"
              tickFormatter={(valor: string) => formatPeriodoCorto(valor)}
              tick={{ fill: CHART.eje, fontSize: 11 }}
              stroke={CHART.grid}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fill: CHART.eje, fontSize: 11 }}
              stroke={CHART.grid}
              width={54}
              tickFormatter={(valor: number) => formatMetricValue(valor, 'usd')}
            />
            <Bar
              dataKey="nuevo"
              stackId="mov"
              fill={CHART.serieSuave}
              name="Nuevo MRR"
            />
            <Bar
              dataKey="expansion"
              stackId="mov"
              fill={CHART.serie}
              name="Expansión"
            />
            <Bar
              dataKey="churn"
              stackId="mov"
              fill={CHART.perdida}
              name="Churn"
            />
            <Line
              type="monotone"
              dataKey="mrr"
              stroke={CHART.serie}
              strokeWidth={2}
              dot={false}
              connectNulls={false}
              name="MRR"
            />
          </ComposedChart>
        </div>
      </div>
      <p className="text-micro text-muted-strong">
        Línea: MRR del mes. Barras: nuevo y expansión hacia arriba, churn hacia
        abajo. Un mes sin cargar corta la línea en vez de dibujar un cero.
      </p>
    </figure>
  )
}
